import React, { FC } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { Paper, Typography, useTheme } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import { renderActiveShape } from './charts/ORPerformancePieChart';

const COLORS = ['#00A7F7', '#FFBB28', '#2B3C87'];
const CHART_HEIGHT = 280;

export const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Paper>
        <Box p={2}>
          <Typography gutterBottom>{payload[0].name}</Typography>
          {payload.map((p, i) => (
            <>
              <Box key={i} display="flex" alignItems="center" style={{ width: '16ch' }}>
                <Typography variant="h4">{`${Math.round(p.value)} minute${
                  Math.round(p.value) % 10 == 1 ? '' : 's'
                }`}</Typography>
              </Box>
            </>
          ))}
        </Box>
      </Paper>
    );
  }

  return null;
};

const renderJourneyLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, payload, percent }) => {
  if (!payload?.value || payload.value < 1 || percent < 0.12) {
    return null;
  }
  const RADIAN = Math.PI / 180;
  const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
  const x = cx + radius * Math.cos(-midAngle * RADIAN);
  const y = cy + radius * Math.sin(-midAngle * RADIAN);
  const label = `${Math.round(payload.value)}m`;
  return (
    <>
      <text
        x={x}
        y={y}
        fill="#fff"
        fontWeight="bold"
        fontSize={11}
        textAnchor="middle"
        dominantBaseline="central"
        stroke="#1a2553"
        strokeWidth={4}
        strokeLinejoin="round"
      >
        {label}
      </text>
      <text x={x} y={y} fill="#fff" fontWeight="bold" fontSize={11} textAnchor="middle" dominantBaseline="central">
        {label}
      </text>
    </>
  );
};

const PacuJourneyChart: FC<{ pacuTimes: any }> = ({ pacuTimes }) => {
  const theme = useTheme();
  const [activeIndex, setActiveIndex] = React.useState(0);

  const data = pacuTimes?.map(e => ({ name: e.name, value: Math.round(e.value) })) || [];

  const total = Math.round(data.reduce((acc, cur) => acc + cur.value, 0));

  return (
    <Box display="flex" flexDirection="column" height={CHART_HEIGHT} minWidth={0}>
      <Box flex={1} minHeight={0} minWidth={0}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              label={renderJourneyLabel}
              labelLine={false}
              startAngle={90}
              endAngle={-270}
              dataKey="value"
              data={data}
              innerRadius="52%"
              outerRadius="78%"
              activeShape={props => renderActiveShape(props, total, `${total === 1 ? 'Minute' : 'Minutes'}`)}
              activeIndex={activeIndex}
              paddingAngle={2}
            >
              {data.map((entry, index) => (
                <Cell stroke="#1A2553" key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload, label }) => (
                <CustomTooltip active={active} payload={payload} label={label} />
              )}
              cursor={{ fill: 'rgba(255,255,255,0.15)' }}
              contentStyle={{
                backgroundColor: theme.palette.background.paper,
                borderRadius: 4,
                border: 0,
              }}
            />
          </PieChart>
        </ResponsiveContainer>
      </Box>
      <Box display="flex" flexWrap="wrap" style={{ gap: 8 }} pt={0.5} flexShrink={0}>
        {data.map((entry, index) => (
          <Box key={entry.name} display="flex" alignItems="center" style={{ gap: 4, minWidth: 0 }}>
            <Box
              width={8}
              height={8}
              borderRadius="50%"
              bgcolor={COLORS[index % COLORS.length]}
              style={{ flexShrink: 0 }}
            />
            <Typography variant="caption" title={entry.name} style={{ lineHeight: 1.25 }}>
              {entry.name}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

export default PacuJourneyChart;
